import { buildCloseReasonHint } from '../../../../common/closeReasonHint'
import {
  getFinishedPaymentStatusOptions,
  getMatchingPaymentStatusOption,
  MatchingPaymentTicket,
  MatchingPaymentStatusOption,
  MatchingPayoutStatusOptions,
  MatchingStatusOptions,
  MatchingFile,
} from '../../core/_models'
import { convertUTCtoMSK } from '../../../../../utils'
import { useCallback, useEffect, useMemo, useState, MouseEvent } from 'react'
import Select, { SingleValue } from 'react-select'
import { SelectStyles } from '../../../../../components/Select/Select.theme'
import { useThemeMode } from '../../../../../_metronic/partials'
import {
  EMatchingPaymentTicketStatus,
  EMatchingPayoutTicketStatus,
  EMatchingStatus,
} from '../../../../../types/enums'
import { formatSum } from '../../../../../utils/formatSum'
import { AmountChangeBadge } from '../../../../../components/AmountChangeBadge'
import { ClientCell } from '../../../../common/ClientCell'
import { AntifraudSignal } from '../common/AntifraudSignal'
import { ResendCallbackButton } from '../common/ResendCallbackButton'
import { DisputeResolutionBadge } from '../common/DisputeResolutionBadge'
import { ReceiptCell } from '../common/ReceiptCell'
import InfoHint from '../../../../common/InfoHint'
import { Table } from '../../../../../components/Table'
import { TableHead } from '../../../../../components/Table/TableHead'
import { TableBody } from '../../../../../components/Table/TableBody'
import { KTIcon } from '../../../../../_metronic/helpers'
import clsx from 'clsx'
import { MatchingTicketLink } from '../../../../common/MatchingTicketLink'
import { ChangeCreditedAmountModal } from '../../../settlement'

const knownStatuses = getFinishedPaymentStatusOptions()

const selectableStatuses = ['COMPLETED', 'EXPIRED']

const statusOptions: Partial<Record<EMatchingPaymentTicketStatus, MatchingPaymentStatusOption[]>> =
  {
    COMPLETED: [knownStatuses.COMPLETED, knownStatuses.EXPIRED],
    EXPIRED: [knownStatuses.COMPLETED, knownStatuses.EXPIRED],
    DECLINED: [knownStatuses.DECLINED, knownStatuses.EXPIRED],
  }

/**
 * CPAYMENT-1669: красным помечаем только реальную аномалию — клиент перевёл не ту сумму, которую
 * ему выдали. Сам факт кастомизации/варианта — нейтральный бейдж «было → стало»: раньше красным
 * горел каждый такой тикет, и сигнал не отличал штатный вариант от недоплаты.
 */
export const isPaidAmountUnexpected = (ticket: {
  status: string
  amount: number | null
  expectedAmount: number | null
  requisiteAmount?: number | null
}): boolean => {
  if (ticket.status !== 'COMPLETED' || ticket.amount === null) {
    return false
  }

  const issued = ticket.requisiteAmount ?? ticket.expectedAmount

  return issued !== null && Math.abs(Number(ticket.amount) - Number(issued)) >= 0.01
}

interface FinishedPaymentTicketItemProps {
  ticket: MatchingPaymentTicket
  onStatusChange: (t: MatchingPaymentTicket | null, status: MatchingPaymentStatusOption) => void
  visibility: Record<string, boolean>
  visibleColumns: string[]
  visibleColumnsCount: number
  canManageTickets: boolean
  canManageBills: boolean
  updateBillForTicket: (token: string, file: MatchingFile) => void
  updateBillStatus: (
    token: string,
    billId: MatchingFile['id'],
    status: MatchingFile['status']
  ) => void
  canRollbackCompleted: boolean
  canResend: boolean
  canChangeAmount: boolean
  onAmountChanged: (token: string, amount: number) => void
  canOpenDisputeJournal: boolean
}

const FinishedPaymentTicketItem = ({
  ticket,
  onStatusChange,
  visibility,
  visibleColumns,
  visibleColumnsCount,
  canManageTickets,
  canManageBills,
  updateBillForTicket,
  updateBillStatus,
  canRollbackCompleted,
  canResend,
  canChangeAmount,
  onAmountChanged,
  canOpenDisputeJournal,
}: FinishedPaymentTicketItemProps) => {
  const onReceiptUploaded = useCallback(
    (file: MatchingFile) => updateBillForTicket(ticket.ticketToken, file),
    [updateBillForTicket, ticket.ticketToken]
  )
  // Строка остаётся в списке: решение по чеку не меняет ни статус завершённого
  // тикета, ни сумму зачисления — поэтому onDecisionAcknowledged здесь не нужен.
  const onReceiptDecided = useCallback(
    (billId: MatchingFile['id'], status: MatchingFile['status']) =>
      updateBillStatus(ticket.ticketToken, billId, status),
    [updateBillStatus, ticket.ticketToken]
  )

  const status = useMemo(() => getMatchingPaymentStatusOption(ticket.status), [ticket])

  // Причина закрытия рядом со статусом: и осознанная отмена клиентом, и истёкший срок приходят
  // одинаковым EXPIRED, поэтому по статусу их не различить. Бэкенд присылает её только у
  // завершённых заявок — у активных подсказка не отрисуется сама собой.
  const closeReasonHint = buildCloseReasonHint(ticket.closeReasonTitle, ticket.clientCancelReasonTitle)
  // CPAYMENT-1750: у роли без права отката на завершённом тикете не остаётся вариантов, кроме
  // текущего статуса, — селект вырождается и ниже рендерится текстом. Обратный EXPIRED → COMPLETED
  // не трогаем: он роли разрешён.
  const options = useMemo(() => {
    const all = status?.value ? statusOptions[status.value] || [] : []
    if (status?.value === 'COMPLETED' && !canRollbackCompleted) {
      return all.filter((option) => option.value !== 'EXPIRED')
    }
    return all
  }, [status, canRollbackCompleted])

  const { mode: theme } = useThemeMode()
  const [selectValue, setSelectValue] = useState(getMatchingPaymentStatusOption(status))

  useEffect(() => {
    const option: MatchingPaymentStatusOption | undefined =
      status && options?.length ? getMatchingPaymentStatusOption(status) : undefined
    setSelectValue(option ? options?.find((el) => el.value === option?.value) : undefined)
  }, [options, status])

  const isAmountChanged = isPaidAmountUnexpected(ticket)
  // Сумма отсутствует у DECLINED/EXPIRED без оплаты — там прочерк, а не «0».
  const displayedAmount = ticket?.amount ?? ticket?.expectedAmount ?? null

  const handleChangeStatus = async (
    status: SingleValue<{
      value: EMatchingPaymentTicketStatus
      label: string
    }>
  ) => {
    onStatusChange({ ...ticket }, status || knownStatuses.DECLINED)
  }

  const [changingAmount, setChangingAmount] = useState(false)
  const hasActions = canResend || canChangeAmount
  const canChangeThisAmount = canChangeAmount && ticket.status === 'COMPLETED'

  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const isExpandable = !!ticket.relatedPayoutTicket

  const toggleOpen = useCallback(
    (e: MouseEvent<HTMLTableRowElement>) => {
      if (!isExpandable) return
      if ((e.target as HTMLElement)?.closest('a, button, [role="button"], input, select, .react-select__control')) return
      setIsExpanded((prev) => !prev)
    },
    [isExpandable]
  )

  return (
    <>
      <tr className={clsx(isExpandable && 'cursor-pointer')} onClick={toggleOpen}>
        <td className='text-center align-middle' style={{ width: 32 }}>
          {isExpandable ? (
            <KTIcon
              iconName={isExpanded ? 'minus-square' : 'plus-square'}
              className='fs-3 text-muted'
            />
          ) : null}
        </td>

        {visibility.ticketToken && (
          <td>
            <p className='mb-1'><MatchingTicketLink token={ticket.ticketToken} /></p>
            {/* CPAYMENT-1615: тикет закрыт решением по спору, а не штатным флоу — видно сразу. */}
            <div className='mb-3'>
              <DisputeResolutionBadge
                ticketToken={ticket.ticketToken}
                disputeStatus={ticket.disputeStatus}
                linkable={canOpenDisputeJournal}
              />
            </div>
            <div className='d-flex-column'>
              <p className='fw-bold fs-9'>ID транзакции:</p>
              <span className='fs-9'>{ticket?.merchantTransactionId ?? '-'}</span>
            </div>
          </td>
        )}

        {visibility.amount && (
          <td className={clsx(isAmountChanged && 'text-danger')}>
            <div>{displayedAmount !== null ? formatSum(displayedAmount) : '-'}</div>
            <AmountChangeBadge
              requestedAmount={ticket.expectedAmount}
              issuedAmount={ticket.requisiteAmount ?? null}
              source={ticket.amountChangeSource ?? null}
            />
          </td>
        )}

        {visibility.paymentMethod && <td>{ticket.paymentMethod || '-'}</td>}

        {visibility.createdDate && (
          <td>{ticket.createdAt ? convertUTCtoMSK(ticket.createdAt) : '-'}</td>
        )}

        {visibility.completionDate && (
          <td>{ticket.completionDate ? convertUTCtoMSK(ticket.completionDate) : '-'}</td>
        )}

        {visibility.client && (
          <ClientCell externalId={ticket.externalUserId} token={ticket.clientToken} paymentTicketToken={ticket.ticketToken} />
        )}

        {visibility.merchant && <td>{ticket.merchant || '-'}</td>}

        {/* CPAYMENT-1712: сотрудник, обработавший тикет. Прочерк — обработки сотрудником не было
            (полный автомат) либо тикет закрыт до выката: ретроспективы по verify чека в БД нет. */}
        {visibility.executor && <td>{ticket.executor || '-'}</td>}

        {visibility.requisite && (
          <td>
            {ticket.requisite ? (
              <div className="text-dark">
                {ticket.requisite.identifier && (
                  <div>
                    <strong>Реквизит:</strong> {ticket.requisite.identifier}
                  </div>
                )}
                {ticket.requisite.bank && (
                  <div>
                    <strong>Банк:</strong> {ticket.requisite.bank}
                  </div>
                )}
                {ticket.requisite.cardholder && (
                  <div>
                    <strong>Держатель:</strong> {ticket.requisite.cardholder}
                  </div>
                )}
              </div>
            ) : (
              <span className="text-muted">-</span>
            )}
          </td>
        )}

        {visibility.file && (
          <td className='downloadLink text-center align-middle'>
            <ReceiptCell
              ticketToken={ticket.ticketToken}
              files={ticket.files}
              merchantId={ticket.merchantId}
              canManageBills={canManageBills}
              showDecisionBadge
              canDecide={canManageBills && ticket.disputeStatus !== 'OPEN'}
              onUploaded={onReceiptUploaded}
              onDecided={onReceiptDecided}
            />
          </td>
        )}

        {visibility.antifraud && (
          <td className='text-center align-middle'>
            <AntifraudSignal signal={ticket.antifraudSignal} />
          </td>
        )}

        {visibility.status && (
          <td className='statusSelect w-200px'>
            {canManageTickets &&
            status &&
            selectableStatuses.includes(status.value) &&
            options.length > 1 ? (
              <div className='d-flex align-items-center justify-content-center'>
                <div className='flex-grow-1'>
                  <Select
                    styles={SelectStyles(theme)}
                    onChange={handleChangeStatus}
                    options={options}
                    hideSelectedOptions={true}
                    value={selectValue}
                    menuPosition='fixed'
                    defaultValue={status}
                  />
                </div>
                <InfoHint text={closeReasonHint} />
              </div>
            ) : (
              <p className='text-center mb-0'>
                {status?.label || '-'}
                <InfoHint text={closeReasonHint} />
              </p>
            )}
          </td>
        )}

        {hasActions && (
          <td className='text-end align-middle'>
            {canChangeThisAmount && (
              <button
                type='button'
                className='btn btn-icon btn-bg-light btn-active-color-primary btn-sm me-1 fs-6'
                onClick={() => setChangingAmount(true)}
                title='Изменить сумму зачисления'
                aria-label='Изменить сумму зачисления'
              >
                <KTIcon className='fs-1' iconName='pencil' />
              </button>
            )}
            {canResend && <ResendCallbackButton token={ticket.ticketToken} />}
          </td>
        )}
      </tr>

      {canChangeThisAmount && (
        <ChangeCreditedAmountModal
          show={changingAmount}
          token={ticket.ticketToken}
          currentAmount={ticket.amount}
          onClose={() => setChangingAmount(false)}
          onChanged={(amount) => onAmountChanged(ticket.ticketToken, amount)}
        />
      )}

      {isExpanded && ticket.relatedPayoutTicket && (
        <tr>
          <td colSpan={visibleColumnsCount + 1 + (hasActions ? 1 : 0)} className='p-0'>
            <div className='m-4 border bg-background-light'>
              <Table>
                <TableHead>
                  <tr>
                    <th className='min-w-200px ps-8'><small>Токен выплаты</small></th>
                    <th className='min-w-100px'><small>Сумма</small></th>
                    <th className='min-w-140px'><small>Статус</small></th>
                    <th className='min-w-160px'><small>Статус мэтчинга</small></th>
                    <th className='min-w-160px'><small>Дата создания</small></th>
                    <th className='min-w-140px'><small>Мерчант</small></th>
                    <th className='min-w-200px'><small>ID транзакции</small></th>
                  </tr>
                </TableHead>
                <TableBody>
                  <tr>
                    <td className='ps-8'>
                      <MatchingTicketLink token={ticket.relatedPayoutTicket.token} type='payout' />
                    </td>
                    <td>{ticket.relatedPayoutTicket.amount ?? '-'}</td>
                    <td>
                      {ticket.relatedPayoutTicket.status
                        ? MatchingPayoutStatusOptions[ticket.relatedPayoutTicket.status as EMatchingPayoutTicketStatus]?.label ?? ticket.relatedPayoutTicket.status
                        : '-'}
                    </td>
                    <td>
                      {ticket.relatedPayoutTicket.matchingStatus
                        ? MatchingStatusOptions[ticket.relatedPayoutTicket.matchingStatus as EMatchingStatus]?.label ?? ticket.relatedPayoutTicket.matchingStatus
                        : '-'}
                    </td>
                    <td>
                      {ticket.relatedPayoutTicket.createdAt
                        ? convertUTCtoMSK(ticket.relatedPayoutTicket.createdAt)
                        : '-'}
                    </td>
                    <td>{ticket.relatedPayoutTicket.merchant ?? '-'}</td>
                    <td>{ticket.relatedPayoutTicket.merchantTransactionId ?? '-'}</td>
                  </tr>
                </TableBody>
              </Table>
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export default FinishedPaymentTicketItem
